/**
 * TOTP verify page — every login after the first.
 *
 * Two paths:
 *   - Authenticator: POST /auth/totp/verify { code }
 *   - Recovery code: POST /auth/recovery/verify { code }
 *
 * On 409 totp_not_enrolled the server is telling us the operator
 * actually needs to enroll first — bounce them to /totp/enroll.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  asApiError,
  postRecoveryVerify,
  postTotpVerify,
} from "@/lib/api";
import { safeRedirectPath } from "@/lib/redirect";
import { useAuth } from "@/lib/use-auth";
import { AuthShell } from "@/components/auth/AuthShell";
import { OtpInput } from "@/components/auth/OtpInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Mode = "totp" | "recovery";

export function TOTPVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [mode, setMode] = useState<Mode>("totp");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(value: string) {
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "totp") {
        await postTotpVerify(value);
      } else {
        await postRecoveryVerify(value);
      }
      await refresh();
      navigate(safeRedirectPath(params.get("next")) ?? "/", { replace: true });
    } catch (err) {
      const body = asApiError(err);
      if (body?.error === "totp_not_enrolled") {
        navigate("/totp/enroll", { replace: true });
        return;
      }
      if (body?.error === "account_locked") {
        setError(
          "Too many failed attempts. This account is locked — try again in 15 minutes.",
        );
      } else if (body?.error === "invalid_code") {
        setError("That code didn't match. Try again.");
      } else if (body) {
        setError(`Verification failed (${body.error}).`);
      } else {
        setError("Verification failed. Check your network.");
      }
      // Empty boxes, ready for the next code.
      if (mode === "totp") setCode("");
    } finally {
      setSubmitting(false);
    }
  }

  const complete = mode === "totp" ? code.length === 6 : code.trim() !== "";

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (complete && !submitting) void submit(code.trim());
  }

  return (
    <AuthShell
      title="Two-factor verification"
      description={
        mode === "totp"
          ? "Enter the 6-digit code from your authenticator app."
          : "Enter one of your one-time recovery codes."
      }
    >
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        {mode === "totp" ? (
          <OtpInput
            label="Authenticator code"
            value={code}
            onChange={setCode}
            onComplete={(full) => void submit(full)}
            readOnly={submitting}
            autoFocus
          />
        ) : (
          <div className="space-y-2">
            <Label htmlFor="code">Recovery code</Label>
            <Input
              id="code"
              autoComplete="one-time-code"
              maxLength={64}
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={submitting}
              placeholder="abcd-1234-…"
            />
          </div>
        )}
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {error}
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3">
          <Button asChild variant="ghost" className="text-primary hover:text-primary">
            <Link to="/login" replace>
              <ArrowLeft />
              Back
            </Link>
          </Button>
          <Button type="submit" className="min-w-36" disabled={submitting || !complete} size="lg">
            {submitting ? "Verifying…" : "Verify"}
          </Button>
        </div>
        <Button
          type="button"
          variant="link"
          className="w-full"
          onClick={() => {
            setMode((m) => (m === "totp" ? "recovery" : "totp"));
            setCode("");
            setError(null);
          }}
        >
          {mode === "totp"
            ? "Use a recovery code instead"
            : "Back to authenticator code"}
        </Button>
      </form>
    </AuthShell>
  );
}

export default TOTPVerifyPage;
